/**
 * signInThrottle.js
 *
 * NOTE: the client-side sign-in *blocking* is intentionally DISABLED.
 *
 * It was added to soften Zoho's OAuth throttle (~10 authorize requests / 10 min /
 * account), but a browser can't reliably count authorize requests — they happen across
 * full-page redirects, inside the Catalyst iframe, across multiple tabs, AND across
 * Edge/Chrome InPrivate windows that SHARE localStorage. That produced false positives,
 * blocking legitimate first sign-ins. The real cause of the retry storms is the
 * `.in`/`.com` data-center mismatch (sessions not persisting / logout not clearing the
 * session); fixing that removes the need for this guard. Zoho's own limit is the backstop.
 *
 * We keep recordAttempt()/hadRecentAttempt() purely for telemetry (so useAuth can flag
 * "signed in but session didn't stick"). getThrottleState() never blocks.
 */

const KEY = 'larynx_signin_attempts';
const WINDOW_MS = 2 * 60 * 1000;

function read() {
  try {
    const arr = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(arr) ? arr.filter((t) => typeof t === 'number') : [];
  } catch {
    return [];
  }
}

export function recordAttempt(now = Date.now()) {
  try {
    const arr = read().filter((t) => now - t < WINDOW_MS);
    arr.push(now);
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch {
    /* storage disabled — fine, telemetry is best-effort */
  }
}

export function clearAttempts() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem('larynx_signin_block'); // clear any stale block from the old guard
  } catch {
    /* noop */
  }
}

// True if a sign-in was initiated within `windowMs` (default 2 min) — lets the session
// check distinguish a "just tried, didn't stick" 401 from a normal anonymous visit.
export function hadRecentAttempt(windowMs = 2 * 60 * 1000, now = Date.now()) {
  return read().some((t) => now - t < windowMs);
}

// Blocking is disabled — always allow sign-in. (Kept so callers/imports are stable.)
export function getThrottleState() {
  return { blocked: false, reason: null, retryInMs: 0 };
}
